import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Observable, tap, map } from "rxjs";
import { ApiResponse } from "../models/apiResponse";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { JwtHelperService } from "@auth0/angular-jwt";
import { LoginRequestDTO } from "../dtos/auth/login-request.dto";
import { ResetPasswordDTO } from "../dtos/auth/reset-password.dto";
import { UserDTO } from "../dtos/user.dto";
import { TokenDTO } from "../dtos/auth/token.dto";
import { TesteeLoginRequestDTO } from "../dtos/auth/testee-login-request.dto";

@Injectable({ providedIn: 'root' })
export class AuthService {

    apiUrl = environment.apiUrl;
    private _accessToken: string;
    private _expiresAt: number;
    private _expirationDate: Date;
    private tokenExpirationTimer: any;
    private expiryWarningTimer: any;

    get accessToken() {
        return this._accessToken;
    }

    constructor(private http: HttpClient,
        private router: Router,
        private messageService: MessageService
    ) { }

    login(model: LoginRequestDTO): Observable<boolean> {
        return this.http.post<ApiResponse>(`${this.apiUrl}/auth/login`, model).pipe(
            map((response: ApiResponse) => {
                return this.handleAuthentication(response.result);
            })
        );
    }

    // [AUTH-01/25] The single source of truth for role claims. The API emits `role` as a string
    // for one role and an array for several — normalize to an array; a missing or malformed
    // token yields no roles instead of throwing. RoleService and the guards build on this.
    roles(): string[] {
        try {
            const role = new JwtHelperService().decodeToken(this.accessToken)?.role;
            if (Array.isArray(role)) {
                return role;
            }
            return typeof role === 'string' ? [role] : [];
        } catch {
            return [];
        }
    }

    isTestee(): boolean {
        return this.roles().includes('testee');
    }

    // [AUTH-02] An authenticated non-testee landing on the login page or site root is sent to
    // their app home; every other URL (including testee links) is left to normal route
    // resolution. The old version matched '/login' anywhere in the href, special-cased a dead
    // '/mfa' route, and re-navigated verify-testee-login to the URL it was already on.
    autoLogin() {
        const path = window.location.pathname;
        if (this.isAuthenticated() && !this.isTestee() && (path === '/' || path.startsWith('/auth/login'))) {
            this.router.navigate(['/app']);
        }
    }

    // Two session stores: tester/admin sessions persist in localStorage (shared by every tab),
    // testee sessions live in sessionStorage (THIS tab only). A testee email link opened in a
    // browser where a tester is logged in must shadow the tester's session in that one tab —
    // not overwrite it for every tab, which silently swapped the tester's requests onto the
    // testee's JWT. The tab's active session is whichever store activeStore() picks.
    private activeStore(): Storage {
        return sessionStorage.getItem('accessToken') !== null ? sessionStorage : localStorage;
    }

    // Whether this tab has any stored session — lets ErrorInterceptor distinguish "session
    // died" (show the expired toast) from "was never logged in".
    hasStoredSession(): boolean {
        return this.activeStore().getItem('accessToken') !== null;
    }

    // The token of this tab's active session, for ErrorInterceptor's stale-401 check: a 401
    // may only end the session whose token made the request. Without this, two parallel 401s
    // from a dead testee session cascade — the first logout clears sessionStorage, the second
    // falls through to localStorage and kills the tester's session in every tab.
    storedToken(): string | null {
        return this.activeStore().getItem('accessToken');
    }

    // The stored user for this tab's session. Components must read the user through this,
    // never from localStorage directly — a testee tab would get the tester's user.
    storedUser(): UserDTO | null {
        try {
            const user = this.activeStore().getItem('user');
            return user ? JSON.parse(user) : null;
        } catch {
            return null;
        }
    }

    // [AUTH-03] ACCEPTED RISK — documented decision: the JWT + user live in web storage
    // (localStorage for testers, sessionStorage for testees), which any XSS payload can read.
    // Moving to httpOnly cookies would require BE session/CSRF work that is out of scope for
    // now. Mitigations in place: Angular's default output escaping (no innerHTML of user
    // content in this app), 12-hour token lifespan (AUTH-44), and issuer/audience-validated
    // tokens (AUTH-48). Revisit if a refresh-token flow is added.
    handleAuthentication(tokenDTO: TokenDTO): boolean {
        // [AUTH-04] Decode BEFORE persisting (base64url-safe via JwtHelperService — raw atob()
        // throws on '-'/'_' payloads) so an unreadable token is never stored as a session.
        let exp: number;
        let isTesteeSession: boolean;
        try {
            const decoded = new JwtHelperService().decodeToken(tokenDTO.accessToken);
            exp = +decoded.exp;
            const role = decoded.role;
            isTesteeSession = (Array.isArray(role) ? role : [role]).includes('testee');
        } catch {
            this.logout();
            throw new Error('Received an unreadable session token. Please try logging in again.');
        }

        const store = isTesteeSession ? sessionStorage : localStorage;

        // A tester logging in inside a tab that carried a testee session must drop that
        // sessionStorage shadow, or it would keep masking the new localStorage session.
        if (!isTesteeSession) {
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('user');
        }

        store.setItem('accessToken', tokenDTO.accessToken);
        store.setItem('user', JSON.stringify(tokenDTO.user));
        this._accessToken = tokenDTO.accessToken;
        this._expiresAt = exp;
        this._expirationDate = new Date(this._expiresAt * 1000);
        this.autoLogout(this._expirationDate.getTime() - new Date().getTime());

        return isTesteeSession;
    }

    // [AUTH-07] There is deliberately no refresh flow (the dead refreshToken DTO field is gone);
    // instead the user is warned 5 minutes before the 12-hour token expires so the hard logout
    // never lands mid-keystroke without notice.
    autoLogout(expirationDuration: number) {
        if (this.tokenExpirationTimer) {
            clearTimeout(this.tokenExpirationTimer);
        }
        if (this.expiryWarningTimer) {
            clearTimeout(this.expiryWarningTimer);
        }

        if (expirationDuration <= 0) {
            return;
        }

        const warningLeadTime = 5 * 60 * 1000;
        if (expirationDuration > warningLeadTime) {
            this.expiryWarningTimer = setTimeout(() => {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Session expiring',
                    detail: 'Your session will expire in 5 minutes. Please finish or save your work.',
                    life: 30000
                });
            }, expirationDuration - warningLeadTime);
        }

        this.tokenExpirationTimer = setTimeout(() => {
            this.logout();
        }, expirationDuration);
    }

    logout() {
        // Clear only the store holding THIS tab's session: a testee logout (or 401) in one
        // tab must not kill a tester's localStorage session in the others.
        const store = this.activeStore();
        store.removeItem('accessToken');
        store.removeItem('user');
        localStorage.removeItem('expDate');
        // [AUTH-58] 'version' is deliberately NOT removed here — it is app-update state, not auth
        // state; clearing it forced a spurious full reload on the next config.json poll.

        if (this.tokenExpirationTimer) {
            clearTimeout(this.tokenExpirationTimer);
        }
        if (this.expiryWarningTimer) {
            clearTimeout(this.expiryWarningTimer);
        }

        this.tokenExpirationTimer = null;
        this.expiryWarningTimer = null;

        this.clearAuthState();
        this.router.navigate(['/auth/login']);
    }

    clearAuthState() {
        this._accessToken = '';
        this._expiresAt = 0;
        this._expirationDate = new Date();
    }

    isAuthenticated(): boolean {
        // An expired/malformed session is removed from ITS OWN store only — an expired testee
        // shadow falls back to the tester's still-valid localStorage session on the next call.
        const store = this.activeStore();
        this._accessToken = store.getItem('accessToken') ?? '';
        let user = store.getItem('user');
        let userFirstName = user != null ? JSON.parse(user) : '';

        if (this._accessToken === '' || userFirstName === '') {
            this.clearAuthState();
            return false;
        }

        try {
            const helper = new JwtHelperService();
            const decodedToken = helper.decodeToken(this._accessToken);
            this._expiresAt = decodedToken.exp;
            this._expirationDate = new Date(this._expiresAt * 1000);

            if (new Date() >= this._expirationDate) {
                store.removeItem('accessToken');
                store.removeItem('user');
                this.clearAuthState();
                return false;
            }

            this.autoLogout(this._expirationDate.getTime() - new Date().getTime());
            return true;
        } catch {
            store.removeItem('accessToken');
            store.removeItem('user');
            this.clearAuthState();
            return false;
        }

    }

    register(user: UserDTO): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/auth/register`, user);
    }

    // [AUTH-06] Query values must be encoded with encodeURIComponent — NOT Angular HttpParams,
    // whose default codec leaves '+' unencoded, so name+tag@gmail.com would still arrive at the
    // API as "name tag@gmail.com".
    resendVerificationEmail(email: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/auth/resend-verification-email?email=${encodeURIComponent(email)}`);
    }

    verifyEmail(userId: string, code: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/auth/verify-email?userId=${encodeURIComponent(userId)}&code=${encodeURIComponent(code)}`);
    }

    forgotPassword(email: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/auth/forgot-password?email=${encodeURIComponent(email)}`);
    }

    resetPassword(userId: string, token: string, newPassword: string): Observable<any> {
        let model: ResetPasswordDTO = {
            userId: userId,
            token: token,
            newPassword: newPassword
        };
        return this.http.post<any>(`${this.apiUrl}/auth/reset-password`, model);
    }

    verifyTesteeLogin(testeeId: string, code: string): Observable<any> {
        var testeeLoginRequestDTO = new TesteeLoginRequestDTO();
        testeeLoginRequestDTO.testeeId = testeeId;
        testeeLoginRequestDTO.code = code;
        return this.http.post<any>(`${this.apiUrl}/auth/verify-testee-login`, testeeLoginRequestDTO).pipe(
            tap({
                next: (response: ApiResponse) => {
                    this.handleAuthentication(response.result);
                }
            })
        );
    }
}   